/**
 * 🔴🔴 AVISO É UMA LINHA; O DETALHE SÓ APARECE AO CLICAR (Matheus, 09/10/2026 — regra inviolável:
 * *"cuidado com a poluição na tela, peça para o cc colocar os avisos e ao clicar ele expande, torne isso como regra
 * inviolavel"*).
 *
 * ✅ Fechado por padrão: ícone + a frase do FATO (≤ ~70 caracteres) + o chevron. Ao clicar, abre NO LUGAR (sem modal,
 * sem navegar); clicar de novo recolhe. É um botão com `aria-expanded` (teclado e leitor de tela). Estado em React
 * (❌ zero localStorage).
 * ✅ Vários avisos no mesmo lugar viram UMA linha agregada (`AvisosAgrupados`: "3 avisos sobre este arquivo ›") que
 * abre para as linhas individuais — cada uma também recolhida.
 * ❌ Nunca bloco de texto aberto por padrão, nunca `Alert` com parágrafo inteiro, nunca toast longo.
 *
 * ⚠️ NÃO é um segundo motor de "linha que recolhe": é o `LinhaRecolhivel` (o canônico do #339) com a porta `tom` e
 * `recolhe="sempre"`. A trava que cobra a regra: `src/components/ui/__tests__/aviso-e-uma-linha.test.ts`.
 */
import { Children, isValidElement, type ReactNode } from 'react';

import { LinhaRecolhivel } from './LinhaRecolhivel';
import { CLASSES_DO_TOM, type TomDoAviso } from './tom-do-aviso';

const ICONE: Record<TomDoAviso, string> = { neutro: 'ℹ', atencao: '⚠', perigo: '⚠' };
const COR_DO_ICONE: Record<TomDoAviso, string> = {
  neutro: 'text-ink-3 dark:text-ink-dark-3',
  atencao: 'text-warning',
  perigo: 'text-red-deep dark:text-red-glow',
};

export interface AvisoProps {
  /** O FATO, numa frase curta (≤ ~70 caracteres). É a única coisa visível com o aviso fechado. */
  fato: ReactNode;
  /** O detalhe (lista, números, o que fazer) — só aparece aberto. Sem detalhe, a linha não abre. */
  children?: ReactNode;
  tom?: TomDoAviso;
  /**
   * 🆕 13/10/2026 — porta NOVA e opcional: o toque na linha FAZ uma ação em vez de abrir um detalhe (ex.: o Gantt no celular,
   * "Hoje está 18 dias à frente ›" rola até hoje — decisão do Matheus). Continua uma linha só, com o chevron. Sem ela, nada
   * muda para quem já usava o aviso.
   */
  aoClicar?: () => void;
  className?: string;
  'data-testid'?: string;
}

export function Aviso({ fato, children, tom = 'neutro', aoClicar, className, 'data-testid': testId }: AvisoProps) {
  const titulo = (
    <span className="inline-flex items-start gap-1.5">
      <span aria-hidden className={COR_DO_ICONE[tom]}>
        {ICONE[tom]}
      </span>
      <span>{fato}</span>
    </span>
  );
  if (aoClicar) {
    return (
      <button
        type="button"
        onClick={aoClicar}
        className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm ${CLASSES_DO_TOM[tom]} ${className ?? ''}`}
        data-testid={testId}
        data-tom={tom}
      >
        <span className="min-w-0 flex-1 text-ink-1 dark:text-ink-dark-1">{titulo}</span>
        <span aria-hidden className="shrink-0 text-ink-3 dark:text-ink-dark-3">
          ›
        </span>
      </button>
    );
  }
  if (children == null || children === false) {
    // aviso de uma linha sem detalhe: nada a abrir (e nenhum chevron prometendo o que não existe)
    return (
      <div className={`rounded-lg border px-3 py-2 text-sm ${CLASSES_DO_TOM[tom]} ${className ?? ''}`} data-testid={testId} data-tom={tom}>
        <span className="text-ink-1 dark:text-ink-dark-1">{titulo}</span>
      </div>
    );
  }
  return (
    <LinhaRecolhivel recolhe="sempre" tom={tom} titulo={titulo} className={className} data-testid={testId}>
      <div className="text-sm text-ink-2 dark:text-ink-dark-2">{children}</div>
    </LinhaRecolhivel>
  );
}

/**
 * Vários avisos no mesmo lugar → UMA linha ("3 avisos sobre este arquivo ›"). Com um aviso só, ele aparece direto
 * (agrupar um item seria um clique a mais à toa). Filhos `null`/`false` não contam.
 */
export function AvisosAgrupados({
  sobre,
  children,
  tom = 'neutro',
  'data-testid': testId,
}: {
  /** O complemento da linha agregada: "sobre este arquivo" → "3 avisos sobre este arquivo". */
  sobre: string;
  children: ReactNode;
  /** O tom da linha agregada — o do aviso mais grave dentro dela. */
  tom?: TomDoAviso;
  'data-testid'?: string;
}) {
  const itens = Children.toArray(children).filter(isValidElement);
  if (itens.length === 0) return null;
  if (itens.length === 1) return <>{itens[0]}</>;
  return (
    <LinhaRecolhivel
      recolhe="sempre"
      tom={tom}
      data-testid={testId}
      titulo={
        <span className="inline-flex items-start gap-1.5">
          <span aria-hidden className={COR_DO_ICONE[tom]}>
            {ICONE[tom]}
          </span>
          <span>
            {itens.length} avisos {sobre}
          </span>
        </span>
      }
    >
      <div className="space-y-2">{itens}</div>
    </LinhaRecolhivel>
  );
}
